const React = require('react');
const T = require('prop-types');
const { default: Styled, css } = require('styled-components');
const { NavLink: Link } = require('react-router-dom');

const { default: Button } = require('@mui/material/Button');

const { default: Classes } = require('./styles.scss');
const ManageOwnGroupsContents = require('containers/ManageOwnGroupsContents');
const ManageGroupsSearchField = require('components/ManageGroupsSearchField');
const useForm = require('hooks/useForm');
const MyGroupsPageFormConfig = require('./formConfig');

const { ALL_GROUPS_SORT_TYPE } = require('utils/constants');
const { sortGroups } = require('utils/groups');

const internals = {};
const HEADER_CONTROL_SIZE = 150;
const HEADER_CONTROL_HEIGHT = 56;

module.exports = function MyGroupsPage(props) {

    const {
        rolePermissions,
        allGroupsSortType,
        setAllGroupSortType,
        updateSortPreference
    } = props;

    const {
        OuterBreakoutContainer,
        InnerBreakoutMaxWidthContainer,
        FiltersContainer,
        HeaderControlsForm
    } = internals;

    const [searchText, setSearchText] = React.useState('');

    const handleSortFilterChange = React.useCallback((value) => {

        setAllGroupSortType({
            sortType: value
        });

        updateSortPreference(value);
    }, [setAllGroupSortType, updateSortPreference]);

    return (
        <OuterBreakoutContainer>
            <div className={Classes.wrapper}>
                <FiltersContainer>
                    <HeaderControlsForm
                        allGroupsSortType={allGroupsSortType}
                        canCreateGroups={rolePermissions.canCreateGroups}
                        onSortChange={handleSortFilterChange}
                    />
                </FiltersContainer>
            </div>
            <InnerBreakoutMaxWidthContainer>
                <ManageGroupsSearchField
                    isMe
                    searchText={searchText}
                    setSearchText={setSearchText}
                />
                <ManageOwnGroupsContents
                    searchText={searchText}
                    showChatDetails
                    sortGroups={(groups, sortOptions) => {

                        return sortGroups({
                            groups,
                            sortFilter: allGroupsSortType,
                            ...sortOptions
                        });
                    }}
                    allGroupsSortType={allGroupsSortType}
                    usePageHeading
                />
            </InnerBreakoutMaxWidthContainer>
        </OuterBreakoutContainer>
    );
};

module.exports.propTypes = {
    rolePermissions: T.shape({
        id: T.number,
        roleId: T.number,
        schoolId: T.number,
        homeText: T.string,
        canFilterGroupsByRole: T.bool,
        canCreateGroups: T.bool,
        canSeeChatMessagesNoJoin: T.bool
    }),
    role: T.shape({
        id: T.number,
        name: T.string,
        label: T.string
    }),
    allGroupsSortType: T.string,
    setAllGroupSortType: T.func,
    updateSortPreference: T.func
};

internals.noop = () => {};

internals.buildSortOptions = () => ([
    {
        value: ALL_GROUPS_SORT_TYPE.BY_DATE_UPDATED,
        label: 'Newest Chat'
    },
    {
        value: ALL_GROUPS_SORT_TYPE.BY_NAME,
        label: 'Group Name'
    }
]);

internals.HeaderControlsForm = function HeaderControlsForm(props) {

    const {
        allGroupsSortType,
        canCreateGroups,
        onSortChange
    } = props;

    const formConfig = React.useMemo(() => MyGroupsPageFormConfig.buildFormConfig({
        ALL_GROUPS_SORT_TYPE,
        allGroupsSortType,
        canCreateGroups,
        onSortChange,
        onSubmit: internals.noop,
        renderCreateAction: () => (
            <Button
                component={Link}
                to='/app/classes/create'
                aria-label='create group'
                color='secondary'
                size='small'
                variant='contained'
                disableElevation
                data-focus-outline='radius:10,padding:3,zIndex:1'
                sx={{
                    alignSelf: 'stretch',
                    width: HEADER_CONTROL_SIZE,
                    minWidth: HEADER_CONTROL_SIZE,
                    maxWidth: HEADER_CONTROL_SIZE,
                    height: HEADER_CONTROL_HEIGHT,
                    minHeight: HEADER_CONTROL_HEIGHT,
                    maxHeight: HEADER_CONTROL_HEIGHT,
                    px: 0,
                    py: 0,
                    borderRadius: '10px',
                    border: '1px solid',
                    borderColor: 'secondary.main',
                    boxShadow: 'none',
                    lineHeight: '20px',
                    '&:hover': { boxShadow: 'none' },
                    '&.Mui-focusVisible': { boxShadow: 'none' }
                }}
            >
                Create Group
            </Button>
        ),
        selectFieldStyles: internals.filterFieldStyles
    }), [allGroupsSortType, canCreateGroups, onSortChange]);

    const { Form } = useForm(formConfig);

    return <internals.HeaderControlsFormWrapper>
        {Form}
    </internals.HeaderControlsFormWrapper>;
};

internals.HeaderControlsForm.propTypes = {
    allGroupsSortType: T.string,
    canCreateGroups: T.bool,
    onSortChange: T.func.isRequired
};

internals.OuterBreakoutContainer = Styled.div`
    flex-grow: 1;
    overflow: auto;
    width: 100vw;
    height: 100%;
    position: relative;
    left: 50%;
    right: 50%;
    margin-left: -50vw;
    margin-right: -50vw;
`;

internals.InnerBreakoutMaxWidthContainer = Styled.div`
    max-width: 600px;
    margin: auto;
`;

internals.FiltersContainer = Styled.div`
    width: 100%;
    box-sizing: border-box;
    padding: 20px 20px 0;
`;

internals.HeaderControlsFormWrapper = Styled.div`
    width: 100%;
    display: flex;
    justify-content: center;

    form {
        max-width: 560px;
        align-items: center;
        gap: 0;
    }

    form > div:first-child {
        width: 100%;
        display: flex !important;
        flex-flow: row nowrap !important;
        align-items: stretch;
        justify-content: center;
        gap: 12px !important;
    }

    form > div:first-child > * {
        flex: 0 0 ${HEADER_CONTROL_SIZE}px;
        width: ${HEADER_CONTROL_SIZE}px;
        min-width: ${HEADER_CONTROL_SIZE}px;
        max-width: ${HEADER_CONTROL_SIZE}px;
    }

    @media screen and (max-width: 430px) {
        form > div:first-child {
            gap: 8px !important;
        }
    }
`;

internals.filterFieldStyles = css`
    &.MuiFormControl-root {
        width: ${HEADER_CONTROL_SIZE}px;
        min-width: ${HEADER_CONTROL_SIZE}px;
        max-width: ${HEADER_CONTROL_SIZE}px;
        padding: 0 !important;
    }

    .MuiFilledInput-root {
        height: ${HEADER_CONTROL_HEIGHT}px;
        border-radius: 10px;
        background: rgba(18, 39, 79, 0.06);
        overflow: hidden;

        &:before,
        &:after,
        &:hover:not(.Mui-disabled):before {
            display: none;
        }

        &:hover,
        &.Mui-focused {
            background: rgba(18, 39, 79, 0.08);
        }
    }

    label.MuiInputLabel-root {
        max-width: calc(100% - 24px);
        margin-top: 0 !important;
        height: 16px !important;
        line-height: 16px !important;
        color: #5f6368 !important;
        font-size: 12px;
        transform: translate(12px, 5px) scale(1) !important;
        pointer-events: none;
    }

    .MuiInputLabel-shrink {
        transform: translate(12px, 5px) scale(1) !important;
    }

    .MuiSelect-select.MuiFilledInput-input {
        min-height: 20px;
        padding: 22px 32px 6px 12px !important;
        background: transparent;
        color: ${({ theme }) => theme.palette.text.primary};
        font-size: 16px;
        line-height: 20px;
    }

    .MuiSvgIcon-root {
        right: 8px;
        color: rgba(18, 39, 79, 0.56);
    }
`;
